"use client";

import * as Dialog from "@radix-ui/react-dialog";
import Image from "next/image";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { resolveMediaUrl } from "@/lib/media";
import type { Media } from "@/lib/dto";

type Props = {
  items: Media[];
  title?: string;
  /**
   * Render the photo grid only once the enclosing <details> is opened (by the
   * visitor or "Expand all"). Collapsed itinerary days otherwise put every
   * photo's <img>/srcset into the initial HTML for content nobody sees yet.
   */
  deferUntilOpen?: boolean;
};

export function Gallery({
  items,
  title = "Gallery",
  deferUntilOpen = false,
}: Props) {
  const images = (items ?? []).filter(Boolean) as NonNullable<Media>[];
  const [index, setIndex] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(!deferUntilOpen);

  useEffect(() => {
    if (shown) return;
    const details = rootRef.current?.closest("details");
    if (!details) {
      setShown(true);
      return;
    }
    const onToggle = () => {
      if (details.open) setShown(true);
    };
    onToggle();
    details.addEventListener("toggle", onToggle);
    return () => details.removeEventListener("toggle", onToggle);
  }, [shown]);

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    () =>
      setIndex((i) =>
        i == null ? null : (i - 1 + images.length) % images.length,
      ),
    [images.length],
  );
  const next = useCallback(
    () => setIndex((i) => (i == null ? null : (i + 1) % images.length)),
    [images.length],
  );

  useEffect(() => {
    if (index == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, prev, next]);

  if (!images.length) return null;

  return (
    <div ref={rootRef}>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {(shown ? images.slice(0, 8) : []).map((m, i) => {
          const src = resolveMediaUrl(m.url);
          if (!src) return null;
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => setIndex(i)}
                className="group relative block aspect-[5/4] w-full overflow-hidden rounded-md bg-[color:var(--color-bg)] ring-primary"
                aria-label={`View photo ${i + 1}`}
              >
                <Image
                  src={src}
                  alt={m.alt || ""}
                  fill
                  sizes="(min-width: 1024px) 18vw, 30vw"
                  className="object-cover transition-transform duration-500 hover:scale-105"
                />
              </button>
            </li>
          );
        })}
      </ul>

      <Dialog.Root open={index != null} onOpenChange={(o) => !o && close()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm" />
          <Dialog.Content className="fixed inset-0 z-50 grid place-items-center p-4">
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
            {index != null ? (
              <div className="relative w-full max-w-5xl">
                <div className="relative aspect-[16/10] w-full">
                  <Image
                    src={resolveMediaUrl(images[index].url) ?? ""}
                    alt={images[index].alt || ""}
                    fill
                    sizes="(min-width: 1024px) 1024px, 100vw"
                    className="object-contain"
                  />
                </div>
                {images[index].caption ? (
                  <p className="mt-3 text-center text-sm text-white/80">
                    {images[index].caption}
                  </p>
                ) : null}
              </div>
            ) : null}
            <Dialog.Close
              aria-label="Close gallery"
              className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <X size={18} aria-hidden />
            </Dialog.Close>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous photo"
              className="absolute left-4 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <ArrowLeft size={18} aria-hidden />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next photo"
              className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <ArrowRight size={18} aria-hidden />
            </button>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
